import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import {TablePagination, Select, MenuItem, useTheme} from "@material-ui/core";

import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { MaskedText, DatePicker, ComboSelect } from "../../../controls";
import AircraftDispatchDeviationsValidator from "../validators/AircraftDispatchDeviationsValidator";
import { useAircraftDispatchDeviations, useAddAircraftDispatchDeviation, useUpdateAircraftDispatchDeviation, useDeleteAircraftDispatchDeviation } from "../../../services/aircraft";


const AccessControl = require("../../../AccessControl");
const dateFormat = "DD/MM/YYYY";

const tomorrow = moment.utc().add(1,'day').toISOString();

const getUserName = () => {
  const userInfo = AccessControl.getUserInfo();
  if (userInfo && "firstName" in userInfo && "lastName" in userInfo)
    return (userInfo.firstName + " " + userInfo.lastName);
  else
    return "";
}

const AircraftDispatchList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(AircraftDispatchDeviationsValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    currentUser: getUserName(),
    maintenanceInterface: props.maintenanceInterface,
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddDispatchDeviation = useAddAircraftDispatchDeviation();
  const handleUpdateDispatchDeviation = useUpdateAircraftDispatchDeviation();
  const handleDeleteDispatchDeviation = useDeleteAircraftDispatchDeviation();

  const queryClient = useQueryClient();
  const extraKeys = [props.aircraftTailNumber];
  const dispatchDeviations = useAircraftDispatchDeviations({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        //...(ColumnWidth(140)),
        ...(ColumnWidth(250, undefined, undefined, {paddingLeft: 12, paddingTop: 8}, {paddingLeft: 12})),
        title: "Category",
        field: "deviationCategory",
        editable: props.canEdit ? "always" : "never",
        render: (rowData) => (
          <ComboSelect
            id="deviationCategory"
            group="DISPATCH DEVIATIONS"
            subGroup=""
            fullWidth
            value={rowData.deviationCategory}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="deviationCategory"
            group="DISPATCH DEVIATIONS"
            subGroup=""
            fullWidth
            value={props.value || ""}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 2, paddingTop: 10}, {paddingLeft: 2})),
        title: "Reported On",
        field: "reportedOn",
        type: "date",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(undefined, undefined, undefined, {paddingLeft: 2, paddingTop: 9}, {paddingLeft: 2})),
        title: "Details",
        field: "details",
        initialEditValue: "",
        editable: "always",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 2, paddingTop: 10}, {paddingLeft: 2})),
        title: "Rectified On",
        field: "completedOn",
        type: "date",
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{marginLeft: 50}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        initialFormData={{startCheckDate: tomorrow, endCheckDate: tomorrow, startTime: '00:00', endTime: '00:00', aircraftCheck: '', status: ''}}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
          addRowPosition: "first",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        title="Deferred Defects"
        columns={getColumns({canEdit: state.canEdit})}
        data={dispatchDeviations.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddDispatchDeviation.mutate({record: {...newData, aircraftUuid: props.aircraftUuid, aircraftTailNumber: props.aircraftTailNumber}, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: state.canEdit
            ? (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateDispatchDeviation.mutate({record: {...newData}, oldRecord: oldData, resolve, reject, extraKeys});
            })
            : undefined,
          onRowDelete: state.canEdit
            ? (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteDispatchDeviation.mutate({oldRecord: {...oldData}, resolve, reject, extraKeys});
            })
            : undefined,
        }}
      />
    </div>
  );

}

export default AircraftDispatchList;
